"use client";

import { useState } from "react";
import { askQuestion } from "@/lib/api";
import Nav from "@/components/Nav";
import AuthGuard from "@/components/AuthGuard";

type Source = {
  filename: string;
  page: number;
  score?: number;
  confidence?: string;
  snippet?: string;
};

type Evidence = {
  filename: string;
  page: number;
  chunk_index: number;
  text: string;
};

type Message = {
  question: string;
  answer: string;
  evidence?: Evidence | null;
  sources: Source[];
};

export default function Home() {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeSources, setActiveSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(false);

  async function handleAsk() {
    if (!question.trim() || loading) return;

    const currentQuestion = question.trim();

    setQuestion("");
    setLoading(true);
    setActiveSources([]);

    try {
      const data = await askQuestion(currentQuestion);

      const message: Message = {
          question: currentQuestion,
          answer: data.answer || "",
          evidence: data.evidence || null,
          sources: data.sources || [],
      };

      setMessages((prev) => [...prev, message]);
      setActiveSources(message.sources);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          question: currentQuestion,
          answer: "Hiba történt a kérés feldolgozásakor.",
          sources: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
   <AuthGuard>
    <main className="min-h-screen bg-slate-100 p-6">
     <Nav />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-3xl bg-white p-6 shadow-sm">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">
              Dokumentum AI
            </h1>
            <p className="text-sm text-slate-500">
              Kérdezz a feltöltött dokumentumok alapján.
            </p>
          </div>

<div className="space-y-5">
  {messages.map((message, index) => (
    <div key={index} className="space-y-3">

      <div className="rounded-2xl bg-slate-900 p-4 text-white">
        <div className="text-xs uppercase text-slate-300">
          Kérdés
        </div>
        <div>{message.question}</div>
      </div>

      <div className="rounded-2xl bg-slate-50 p-4 text-slate-800 whitespace-pre-wrap">
        <div className="mb-2 text-xs uppercase text-slate-400">
          Válasz
        </div>

        <p className="whitespace-pre-wrap">
          <span className="whitespace-pre-wrap">{message.answer}</span>
        </p>
      </div>

    {message.evidence && (
      <div className="mt-3 rounded-lg border border-gray-200 p-3 text-sm">
        <div className="font-semibold text-gray-500 text-xs mb-2">Kapcsolódó dokumentumrészlet</div>
        <div className="whitespace-pre-wrap text-gray-700">{message.evidence.text}</div>
        <div className="text-xs text-gray-400 mt-2">
          {message.evidence.filename} · {message.evidence.page}. oldal
     </div>
  </div>
)}

    </div>
  ))}

            {loading && (
              <div className="rounded-2xl bg-slate-50 p-4 text-slate-500">
                Keresés és válasz generálása...
              </div>
            )}
          </div>

          <div className="mt-6">
            <input
              type="text"
              className="w-full rounded-2xl border border-slate-200 p-4 text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-400"
              placeholder="Írd be a kérdésed..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAsk();
                }
              }}
            />
            <button
              onClick={handleAsk}
              disabled={loading || !question.trim()}
              className="mt-3 rounded-2xl bg-slate-900 px-6 py-3 font-medium text-white disabled:opacity-40">
              {loading ? "Dolgozom..." : "Kérdezés"}
            </button>
          </div>
        </section>

      </div>
    </main>
   </AuthGuard>
  );
}
